import { getMultiAgentAuthHeaders, multiAgentBaseUrl } from "./client";
import type { ArtifactContent } from "./types";
import { httpError } from "@/lib/http/apiError";

const BASE = multiAgentBaseUrl;

export type MultiAgentAttachmentKind = "image" | "pdf" | "audio" | "url" | "text" | "code";

/** A multimodal intake attachment. `markdown` is filled after /preprocess. */
export interface MultiAgentAttachment {
  id: string;
  kind: MultiAgentAttachmentKind;
  name: string;
  status: "processing" | "ready" | "error";
  mime?: string;
  language?: string;
  content?: string;        // text/code body or URL string
  data_base64?: string;    // binary (image/pdf/audio)
  markdown?: string;       // resolved by /preprocess
  thumbnailDataUrl?: string; // images only (client preview)
  errorReason?: string;    // why resolution failed (status === "error")
  // URL chips — page title + favicon resolved by /preprocess (optional; the
  // backend fills these for `url` kind, falls back to the raw URL when absent).
  title?: string;
  favicon?: string;
  // Image/audio/pdf — the backend signals when Gemini extraction was cut short
  // (finish_reason !== STOP / clipped to MAX_INPUT_CHARS) so the chip can warn
  // the user the folded text "may be incomplete". Optional → back-compatible.
  truncated?: boolean;
}

/** Resolve ONE attachment (image/pdf/audio/url/text/code) → markdown via the
 *  backend pre-processor (Gemini for binary, fetch for URL, passthrough else). */
export async function preprocessAttachment(input: {
  kind: MultiAgentAttachmentKind;
  name: string;
  mime?: string;
  language?: string;
  content?: string;
  data_base64?: string;
}): Promise<{
  kind: string;
  name: string;
  markdown: string;
  ok?: boolean;
  error?: string | null;
  // OPTIONAL (backward-compatible) — the backend adds these progressively:
  //   url:           title + favicon of the fetched page
  //   image/pdf/audio: truncated / finish_reason when extraction was cut short
  title?: string | null;
  favicon?: string | null;
  truncated?: boolean | null;
  finish_reason?: string | null;
}> {
  const res = await fetch(`${BASE}/api/runs/preprocess`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await httpError(res, "preprocess");
  return res.json();
}

export async function getArtifact(artifactId: string): Promise<ArtifactContent> {
  const res = await fetch(`${BASE}/api/artifacts/${encodeURIComponent(artifactId)}`, {
    method: "GET",
    headers: { ...getMultiAgentAuthHeaders() },
  });
  if (!res.ok) throw await httpError(res, "artifact");
  return res.json();
}

/** Coverage entry shape of a test run (mirrors the dev-agent `coveredClasses` shape). */
export interface MultiAgentCoveredClass {
  name: string;
  coverage_pct: number | null;
  covered_lines: number[];
  uncovered_lines: number[];
  num_covered: number;
  num_uncovered: number;
}

/** Launch a server-side `sf lightning dev` preview for a generated LWC and get
 *  back the org preview URL to open in a new tab. Credentials reuse the chat's
 *  org session (backend resolves them); the body is just the component bundle.
 *  Returns { preview_id, preview_url, instance_url }. */
export async function startLwcPreview(input: {
  component_name: string;
  files: Record<string, string>;
}): Promise<{ preview_id: string; preview_url: string; instance_url: string }> {
  const res = await fetch(`${BASE}/api/lwc-preview`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw await httpError(res, "lwc-preview");
  return res.json();
}

/** Deliberately enable Live Preview (Local Dev) for a specific connected org —
 *  deploys the one `enableLightningPreviewPref` setting. Idempotent. Scopes the
 *  call to `orgId` (overrides the active-org header) so the Connection Manager's
 *  per-card toggle targets the right org. Needs admin / Modify Metadata perms. */
export async function enableLwcPreview(
  orgId?: string,
): Promise<{ enabled: boolean; detail: string }> {
  const headers: Record<string, string> = { ...getMultiAgentAuthHeaders() };
  if (orgId) headers["X-Organization-ID"] = orgId;
  const res = await fetch(`${BASE}/api/lwc-preview/enable`, { method: "POST", headers });
  if (!res.ok) throw await httpError(res, "lwc-preview/enable");
  return res.json();
}

export async function sendFeedback(
  runId: string,
  rating: number,
  comment: string | null,
  reasons: string[] = [],
): Promise<void> {
  const res = await fetch(`${BASE}/api/runs/${encodeURIComponent(runId)}/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getMultiAgentAuthHeaders() },
    body: JSON.stringify({
      rating,
      ...(comment ? { comment } : {}),
      ...(reasons.length > 0 ? { reasons } : {}),
    }),
  });
  if (!res.ok) throw await httpError(res, "feedback");
}

